import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityDisplayName,
} from '@backstage/plugin-catalog-react';
import { visitsApiRef } from '@backstage/plugin-home';
import { searchApiRef, type SearchApi } from '@backstage/plugin-search-react';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { entityLabel } from '../catalog/entityLabel';
import SearchIcon from '@material-ui/icons/Search';
import {
  ACTIONS,
  NAVIGATE_TO,
  PaletteDestination,
  matches,
} from './paletteDestinations';
import { docsExcerpt, type ExcerptPart } from '../search/docsExcerpt';
import { toEntityRef } from '../../visits';

/**
 * The command palette (DX-302).
 *
 * One box that answers three different questions, told apart by what the
 * reader types: a name searches the catalog, a leading `>` lists actions, and
 * a trailing `?` hands the whole line to Ask. That is the target picture's
 * design, and the reason it works is that a reader never has to choose a mode
 * first — they type, and the palette follows.
 *
 * It is built here rather than on Backstage's `SearchModal` because what that
 * component offers is a result list, and a palette is not one: the scope chips
 * change what is searched, the Navigate section is not a search result at all,
 * and `>` and `?` are not queries. Driving the catalog directly is less code
 * than bending a results dialog into this shape, and it keeps the keyboard
 * contract in one place where it can be read.
 *
 * It is also the portal's only search (DX-341). Things — entities, pages,
 * actions — come from the catalog and the portal's own list; what the
 * documentation SAYS comes from the search index, which holds the text of
 * every TechDocs page. Until DX-341 the index had a window of its own behind
 * the search button, while ⌘K, which that button advertised, opened this one:
 * two doors that looked alike and led to different rooms. Every other revenexx
 * portal has one search behind ⌘K, and so does this one now.
 *
 * The two sources are asked at once and never mixed. A catalog hit and an
 * index hit have no score in common, so they cannot be ranked against each
 * other honestly; the documentation gets a section of its own, below the
 * things, and arrives there without moving them.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

/** The scopes, in the order the chips carry them. `tab` steps through these. */
const SCOPES = ['All', 'Services', 'APIs', 'Docs', 'Teams', 'Actions'] as const;
type Scope = (typeof SCOPES)[number];

/** A scope the palette can be opened in, for a page that opens it (DX-306). */
export type PaletteScope = Scope;

/**
 * What each scope asks the catalog for.
 *
 * `Actions` and `Docs` ask the catalog nothing: the first is a fixed list, and
 * the second reads the search index, which holds what the documentation says
 * rather than which entities have some.
 */
const SCOPE_FILTER: Record<Scope, Record<string, unknown> | undefined> = {
  All: {},
  Services: { kind: 'Component' },
  APIs: { kind: 'API' },
  Docs: undefined,
  Teams: { kind: 'Group' },
  Actions: undefined,
};

/**
 * What an untouched palette says under a scope that has nothing to show yet.
 *
 * The portal's pages belong to All alone (DX-349). Under Services they used
 * to fill the empty line, so switching scope changed nothing a reader could
 * see — the chip moved and the list stayed. An empty scope says what typing
 * will find instead.
 */
const SCOPE_HINT: Partial<Record<Scope, string>> = {
  Services: 'Type to search services.',
  APIs: 'Type to search APIs.',
  Docs: 'Type to search the documentation.',
  Teams: 'Type to search teams.',
};

/** How many of the entities a reader opened last an untouched palette offers. */
const RECENT_LIMIT = 5;

/**
 * How many opened entities, overall and of each scope's kind, are worth asking
 * the catalog about. Recent shows five under All and five under each entity
 * scope, so the rest of a hundred-visit history is a request for rows nobody
 * sees. Twice the limit rather than the limit itself, so that entities deleted
 * since they were opened, and the one the reader is on, do not leave it short.
 */
const RECENT_FETCH = RECENT_LIMIT * 2;

/** The heading the documentation's hits stand under, in All and in Docs. */
const DOCS_SECTION = 'In the docs';

/**
 * How many pages of documentation each scope lists before the row that opens
 * all of them. Under All the documentation shares the palette with the things
 * a reader is more often after, so it gets a handful; under Docs it is all the
 * palette shows.
 */
const DOCS_UNDER_ALL = 5;
const DOCS_UNDER_DOCS = 10;

/**
 * Only the fields the rows and the matcher read. The catalog holds hundreds of
 * entities and each carries its full spec, relations and annotations —
 * fetching all of that to show a name and an owner is most of the request for
 * none of the answer.
 */
const ROW_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.description',
  'metadata.tags',
  'spec.owner',
  'spec.type',
  // A team from the GitHub organisation names itself here (DX-202).
  'spec.profile.displayName',
];

/** The kind a scope lists, lower-cased the way an entity ref carries it. */
const kindOf = (scope: Scope): string | undefined =>
  (SCOPE_FILTER[scope]?.kind as string | undefined)?.toLowerCase();

/** The kinds the entity scopes list: component, api, group. */
const SCOPE_KINDS = SCOPES.map(kindOf).filter(Boolean) as string[];

/** Entity refs compare without regard to case, the way the catalog reads them. */
const sameRef = (ref: string) => ref.toLocaleLowerCase('en-US');

const useStyles = makeStyles(theme => ({
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.modal,
    background: 'rgba(11, 5, 34, 0.45)',
    display: 'flex',
    justifyContent: 'center',
    // Not centred vertically: the box grows downwards as results arrive, and a
    // centred one would slide under the reader's eye as it does.
    paddingTop: '12vh',
  },
  panel: {
    width: 'min(640px, calc(100vw - 32px))',
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.background.paper,
    borderRadius: 'var(--radius-lg, 12px)',
    boxShadow: 'var(--shadow-card-hover)',
    overflow: 'hidden',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'none',
    font: 'inherit',
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  esc: {
    flexShrink: 0,
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 'var(--radius-sm, 4px)',
    border: `1px solid ${theme.palette.divider}`,
    fontSize: '0.7rem',
    fontFamily: 'var(--font-mono)',
    color: theme.palette.text.secondary,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.5, 2.5),
  },
  chip: {
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 'var(--radius-pill, 999px)',
    border: `1px solid ${theme.palette.divider}`,
    background: 'none',
    font: 'inherit',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
  },
  chipActive: {
    borderColor: theme.palette.primary.main,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(0, 0, 1),
  },
  sectionLabel: {
    padding: theme.spacing(1.5, 2.5, 0.5),
    margin: 0,
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    padding: theme.spacing(1, 2.5),
    border: 'none',
    background: 'none',
    font: 'inherit',
    fontSize: '0.875rem',
    textAlign: 'left',
    color: theme.palette.text.primary,
    cursor: 'pointer',
  },
  rowActive: {
    background: theme.palette.action.selected,
  },
  rowTitle: {
    flexShrink: 0,
  },
  rowContext: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
  },
  rowKind: {
    flexShrink: 0,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  // A documentation hit is two lines: where it is, then what it says. A column
  // rather than a row that may wrap — a wrapping row put a long heading on a
  // line of its own and pushed the site down to a second one.
  rowDoc: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 2,
  },
  docLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  // A section's heading can be a sentence. It gives way and the site does not,
  // or a long one pushes the row wider than a phone.
  rowTitleDoc: {
    flex: '0 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // The site keeps its width rather than sharing the shrinking: an ellipsis
  // appears at the first pixel taken, and a shared shrink cut "Billing API" to
  // "Billing A…" beside a heading cut by hundreds. Capped, so a long site name
  // cannot take the heading's whole line on a phone.
  docSite: {
    flex: '0 0 auto',
    maxWidth: '45%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
  },
  docKind: {
    flexShrink: 0,
    marginLeft: 'auto',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  excerpt: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    // A URL or a path is one word as far as wrapping goes; without this it ran
    // past the row's edge and was cut there.
    overflowWrap: 'anywhere',
    color: theme.palette.text.secondary,
  },
  // The matched words by weight, not by a highlighter colour: one accent in
  // the portal, and it is for what a reader can act on (portal-shell.md).
  excerptHit: {
    background: 'none',
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  empty: {
    padding: theme.spacing(3, 2.5),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  docsStatus: {
    margin: 0,
    padding: theme.spacing(1, 2.5),
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  footer: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    padding: theme.spacing(1, 2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  footerSpacer: {
    flex: 1,
  },
  kbd: {
    fontFamily: 'var(--font-mono)',
  },
}));

/** One selectable row, flattened out of the sections so ↑↓ can walk them. */
type Row = {
  key: string;
  title: string;
  /** Owner, system, or whatever places the row — shown next to the title. */
  context?: string;
  /** The owner as a reference, shown by its name in place of `context`. */
  contextRef?: string;
  /** The word on the right: an entity's type, or `Page`. */
  kind?: string;
  to: string;
  section: string;
  /** For a documentation hit, the passage the words were found in. */
  excerpt?: ExcerptPart[];
};

/** One result of the search index, as `SearchApi` answers it. */
type IndexResult = Awaited<ReturnType<SearchApi['query']>>['results'][number];

/** A page of the documentation the index found, as the palette lists it. */
type DocHit = {
  location: string;
  title: string;
  site: string;
  excerpt: ExcerptPart[];
};

/**
 * The index's answer as one hit per page.
 *
 * TechDocs is indexed by section, so a page whose words appear under three of
 * its headings comes back three times. The palette lists places to go, and a
 * page is one place: it is kept once, at its best-ranked section, which is
 * also where its link lands.
 */
const toHits = (results: IndexResult[]): DocHit[] => {
  const seen = new Set<string>();
  return results.flatMap(({ document, highlight }) => {
    // The TechDocs collator adds the site's title to the common fields.
    const doc = document as typeof document & {
      entityTitle?: string;
      name?: string;
    };
    const page = doc.location.split('#')[0];
    if (seen.has(page)) {
      return [];
    }
    seen.add(page);
    const marked = highlight?.fields.text;
    return [
      {
        location: doc.location,
        title: doc.title,
        site: doc.entityTitle ?? doc.name ?? '',
        excerpt: marked
          ? docsExcerpt(marked, highlight.preTag, highlight.postTag)
          : docsExcerpt(doc.text),
      },
    ];
  });
};

const docRow = (hit: DocHit): Row => ({
  key: `${DOCS_SECTION}:${hit.location}`,
  title: hit.title,
  context: hit.site,
  kind: 'Doc',
  to: hit.location,
  section: DOCS_SECTION,
  excerpt: hit.excerpt,
});

/**
 * The section's last row: every hit for the same words, on the search page,
 * already narrowed to the documentation the section was listing.
 */
const allDocsRow = (term: string): Row => ({
  key: `${DOCS_SECTION}:all`,
  title: `All results in the documentation for “${term}”`,
  kind: 'Search',
  to: `/search?${new URLSearchParams({ query: term, 'types[]': 'techdocs' })}`,
  section: DOCS_SECTION,
});

/** The owner an entry names, as a reference; a bare name is a team. */
const ownerRefOf = (e: Entity): string | undefined => {
  const owner = e.spec?.owner;
  if (typeof owner !== 'string' || !owner) return undefined;
  try {
    return stringifyEntityRef(
      parseEntityRef(owner, {
        defaultKind: 'group',
        defaultNamespace: e.metadata.namespace ?? 'default',
      }),
    );
  } catch {
    return undefined;
  }
};

const entityRow = (e: Entity, section = 'Results'): Row => {
  const ns = e.metadata.namespace ?? 'default';
  const kind = e.kind.toLowerCase();
  return {
    key: `${section}:${e.kind}:${ns}/${e.metadata.name}`,
    title: entityLabel(e),
    context:
      e.spec?.owner?.toString().replace(/^group:(default\/)?/, '') ?? undefined,
    contextRef: ownerRefOf(e),
    kind: (e.spec?.type as string) ?? kind,
    to: `/catalog/${ns}/${kind}/${e.metadata.name}`,
    section,
  };
};

const destinationRow = (d: PaletteDestination, section: string): Row => ({
  key: `${section}:${d.to}`,
  title: d.title,
  // Pages say so on the right, wherever in the list they stand; an action
  // does not, because the row already reads as one ("Create a service").
  kind: section === 'Actions' ? undefined : 'Page',
  to: d.to,
  section,
});

export const CommandPalette = ({
  onClose,
  initialScope = 'All',
}: {
  onClose: () => void;
  /** The chip it opens on: All, unless a page opened it for its own subject. */
  initialScope?: Scope;
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const visitsApi = useApi(visitsApiRef);
  const searchApi = useApi(searchApiRef);
  const { pathname } = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<Scope>(initialScope);
  // Tagged with the scope they were asked for, so a reader who switches scope
  // with a term on the line never sees the previous scope's answer drawn under
  // the new chip while the new one is on its way — and with the words, so the
  // documentation can tell an answer to THIS question from the last one.
  const [found, setFound] = useState<{
    scope: Scope;
    term: string;
    items: Entity[];
  }>({
    scope: 'All',
    term: '',
    items: [],
  });
  const [searching, setSearching] = useState(false);
  const [recent, setRecent] = useState<Entity[]>([]);
  // The highlighted row, held by its key rather than its position. Recent
  // arrives after the palette has drawn, and results arrive after the line has
  // changed; a position would then point at whatever row moved into it, so a
  // reader who had stepped down to Catalog pressed Enter on something else.
  // No key, or a key the list no longer holds, means the top row.
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // The two special modes, read off the text rather than held as state: the
  // reader switches by typing and unswitches by deleting, and a mode flag
  // would have to be kept in step with the very characters that define it.
  const isActionMode = query.startsWith('>');
  // A line that opens with `>` is an action, whatever it ends with. Without
  // that exclusion `>how do I create a service?` was read as a question and
  // handed to Ask — carrying the `>` into the query, since only a trailing `?`
  // is stripped. The leading character is the stronger signal: the reader
  // typed it first and deliberately.
  const asksQuestion =
    !isActionMode && query.trim().endsWith('?') && query.trim().length > 1;
  const term = isActionMode ? query.slice(1).trim() : query.trim();

  /*
    The caret goes into the palette when it opens, and back where it was when
    it closes — to the search button, for a reader who opened it there. The
    search window this replaced (DX-341) was a dialog that did this itself; a
    palette that dropped the focus on the page left a keyboard reader starting
    over at its top after every Esc.
  */
  useEffect(() => {
    const opener = document.activeElement;
    inputRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, []);

  /*
    Recent: the entities this reader opened last, for an untouched palette.

    Read from the visit history the app records on every page (DX-349), which
    holds pages as well as entities — only the entities are kept, because a
    reader reaches the portal's pages from the Navigate list beside it. The
    history names each by its ref, so the catalog is asked once for all of
    them: for the title and owner a row shows, and because an entity deleted
    since it was opened is not something to offer — the catalog no longer
    answering for a ref is what drops it.

    Asked for once, when the palette opens, rather than per scope: each scope
    keeps the ones of its own kind, and a request per `tab` would make the
    list redraw under a reader stepping through the chips. Nothing stands in
    for a history that is empty — the first entities the catalog returns would
    look like Recent without being it.
  */
  useEffect(() => {
    let alive = true;
    visitsApi
      .list({
        limit: 100,
        orderBy: [{ field: 'timestamp', direction: 'desc' }],
      })
      .then(visits => {
        // Once per entity, however its ref was spelled: a link typed with
        // capitals is the same entity, and would otherwise be listed twice.
        const seen = new Set<string>();
        const refs = visits.flatMap(v => {
          if (!v.entityRef || seen.has(sameRef(v.entityRef))) {
            return [];
          }
          seen.add(sameRef(v.entityRef));
          return [v.entityRef];
        });
        const perKind = new Map<string, number>();
        const wanted = refs.filter((ref, i) => {
          const kind = sameRef(ref.slice(0, ref.indexOf(':')));
          const n = perKind.get(kind) ?? 0;
          perKind.set(kind, n + 1);
          return (
            i < RECENT_FETCH || (SCOPE_KINDS.includes(kind) && n < RECENT_FETCH)
          );
        });
        return wanted.length
          ? catalogApi
              .getEntitiesByRefs({ entityRefs: wanted, fields: ROW_FIELDS })
              .then(({ items }) => items.filter((e): e is Entity => Boolean(e)))
          : [];
      })
      .then(opened => alive && setRecent(opened))
      .catch(() => alive && setRecent([]));
    return () => {
      alive = false;
    };
  }, [visitsApi, catalogApi]);

  /*
    The catalog, filtered by scope and by what has been typed.

    Nothing is fetched for an empty line: an untouched palette offers Recent
    and where it can take you, and the entities arrive as soon as there is
    something to match them against.

    Not run in action mode either, nor while the line is a question, nor under
    the Docs chip: none of the three is a catalog query — Docs reads the search
    index instead — and searching anyway would flash results under somebody
    who is typing a sentence.
  */
  useEffect(() => {
    if (
      isActionMode ||
      asksQuestion ||
      scope === 'Actions' ||
      scope === 'Docs' ||
      !term
    ) {
      setFound({ scope, term, items: [] });
      setSearching(false);
      return undefined;
    }

    let active2 = true;
    setSearching(true);
    const filter = { ...SCOPE_FILTER[scope] } as Record<string, never>;

    // Waited out rather than sent per keystroke: the effect re-runs on every
    // character, and a ten-letter service name is ten requests of which nine
    // are answered after the reader has already typed past them. 150ms is
    // below what reads as a delay while typing and above the gap between two
    // keys.
    const timer = setTimeout(() => {
      catalogApi
        .getEntities({
          filter: Object.keys(filter).length ? filter : undefined,
          fields: ROW_FIELDS,
        })
        .then(({ items }) => {
          if (!active2) {
            return;
          }
          const q = term.toLowerCase();
          const hit = (e: Entity) =>
            // Locations are bookkeeping, not things a person looks for: the
            // GitHub discovery writes one per repository, named
            // `generated-<hash>`, and there are hundreds. Dropped here rather
            // than in the query because a catalog filter states one kind, and
            // several are encoded as a comma-joined AND — `kind=a,kind=b`
            // asks for an entity that is both, which nothing is.
            e.kind !== 'Location' &&
            // Name and title are what a row shows; description and tags are what
            // a reader remembers. Somebody looking for the gateway may type
            // "routing" or "ai-tooling" rather than "ai-gateway", and a palette
            // that only matches the name answers "no such thing" to a question
            // the catalog can plainly answer.
            [
              e.metadata.name,
              entityLabel(e),
              e.metadata.description ?? '',
              ...(e.metadata.tags ?? []),
            ].some(field => field.toLowerCase().includes(q));

          // Whole-word hits on the name first: typing "nord" should offer the
          // service called nord before the six whose description mentions it.
          const score = (e: Entity) =>
            e.metadata.name.toLowerCase().includes(q) ||
            entityLabel(e).toLowerCase().includes(q)
              ? 0
              : 1;

          setFound({
            scope,
            term,
            items: items
              .filter(hit)
              .sort((a, b) => score(a) - score(b))
              .slice(0, 8),
          });
          setSearching(false);
        })
        .catch(() => {
          if (active2) {
            setFound({ scope, term, items: [] });
            setSearching(false);
          }
        });
    }, 150);

    return () => {
      active2 = false;
      clearTimeout(timer);
    };
  }, [catalogApi, scope, term, isActionMode, asksQuestion]);

  const entities = useMemo(
    () => (found.scope === scope ? found.items : []),
    [found, scope],
  );
  // The catalog has answered the question on the line — not an earlier one,
  // and not the same words under another scope. `searching` cannot say this:
  // it is set inside the effect, a render after the line or the chip changed.
  const catalogAnswered = found.scope === scope && found.term === term;

  /*
    What the documentation says, from the search index (DX-341).

    Asked at the same time as the catalog rather than after it: the two are
    separate stores, and nothing makes one wait for the other. Keyed by the
    words alone, so stepping between All and Docs — or through the scopes in
    between, which ask for no documentation — reuses the answer instead of
    asking again: the two scopes list the same hits, All fewer of them. A
    request the words have moved past is cancelled, not just ignored.

    A failed request is remembered as one. An index that cannot be reached and
    an index that found nothing are different facts, and a palette that showed
    the second for the first would tell a reader the documentation is silent on
    something it may well cover. It is asked again on the next visit to a
    scope that shows documentation, and until that answers, the palette says
    it is searching rather than that it failed.
  */
  const wantsDocs =
    !isActionMode &&
    !asksQuestion &&
    (scope === 'All' || scope === 'Docs') &&
    Boolean(term);
  const [docs, setDocs] = useState<{
    term: string;
    hits: DocHit[];
    failed: boolean;
  } | null>(null);
  const docsAnswered = docs?.term === term && !docs.failed;
  useEffect(() => {
    if (!wantsDocs || docsAnswered) {
      return undefined;
    }
    const request = new AbortController();
    // Waited out like the catalog's request, and for the same reason.
    const timer = setTimeout(() => {
      setDocs(d => (d?.term === term ? null : d));
      searchApi
        .query({ term, types: ['techdocs'] }, { signal: request.signal })
        .then(
          ({ results }) =>
            !request.signal.aborted &&
            setDocs({ term, hits: toHits(results), failed: false }),
        )
        .catch(
          () =>
            !request.signal.aborted &&
            setDocs({ term, hits: [], failed: true }),
        );
    }, 150);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
  }, [searchApi, term, wantsDocs, docsAnswered]);
  const docsAnswer = docs?.term === term ? docs : null;

  const rows = useMemo<Row[]>(() => {
    if (asksQuestion) {
      return [
        {
          key: 'ask',
          title: `Ask: ${query.trim()}`,
          kind: 'Ask',
          to: `/ask?q=${encodeURIComponent(query.trim().replace(/\?$/, ''))}`,
          section: 'Ask',
        },
      ];
    }
    if (isActionMode || scope === 'Actions') {
      return ACTIONS.filter(a => matches(a, term)).map(a =>
        destinationRow(a, 'Actions'),
      );
    }

    /*
      Under the Docs chip, what the documentation says and nothing else.

      Not the entities that have documentation, which is what the catalog could
      answer: a reader searching Docs is asking which PAGE says something, and
      offering the first under a chip that promises the second is the defect
      DX-275 measured one level up — a route that resolves, to the wrong thing.
      DX-302 turned the chip into a handoff to the search window; with that
      window gone (DX-341), the chip lists the pages itself.
    */
    if (scope === 'Docs') {
      const hits = docsAnswer?.hits.slice(0, DOCS_UNDER_DOCS).map(docRow) ?? [];
      return hits.length ? [...hits, allDocsRow(term)] : [];
    }

    // The entities the reader opened last, of the scope's own kind — any kind
    // under All — leaving out the one whose page they are on: it would be the
    // top row, so Enter on an untouched palette went nowhere.
    const kind = kindOf(scope);
    const here = toEntityRef({ pathname });
    const recentRows = recent
      .filter(e => !here || sameRef(stringifyEntityRef(e)) !== sameRef(here))
      .filter(e => !kind || e.kind.toLowerCase() === kind)
      .slice(0, RECENT_LIMIT)
      .map(e => entityRow(e, 'Recent'));

    /*
      Services, APIs and Teams list entities of their kind and nothing else.

      The portal's pages used to follow the reader into every scope: an empty
      line under Services showed the same Navigate list as All, and a term
      matched pages beside the services. The chip moved and the list did not
      narrow, which is the one thing a scope promises (DX-349). Pages are All's.
    */
    if (scope !== 'All') {
      return term ? entities.map(e => entityRow(e)) : recentRows;
    }

    if (!term) {
      return [
        ...recentRows,
        ...NAVIGATE_TO.map(d => destinationRow(d, 'Navigate')),
      ];
    }

    const pages = NAVIGATE_TO.filter(d => matches(d, term));

    /*
      A page whose name IS what was typed goes first.

      Entities otherwise lead, because the catalog is what a palette is mostly
      reached for. But "catalog", "docs" and "status" are page names before
      they are anything else, and matching descriptions and tags means some
      service whose text mentions the word can now outrank the page a reader
      plainly asked for. An exact name match is the one signal strong enough
      to overrule the default order.
    */
    const exactPage = pages.filter(
      d => d.title.toLowerCase() === term.toLowerCase(),
    );
    const otherPages = pages.filter(d => !exactPage.includes(d));

    /*
      The documentation comes last, and not before the catalog has answered.

      The index is the slower of the two, and a row that appears ABOVE others
      moves them under a reader who is already reaching for Enter — which takes
      the top row, whatever it has just become. Below everything, a section
      that arrives late moves nothing. Waiting for the catalog closes the other
      order: an index that answers first would otherwise fill the top of an
      empty list, and the entities would then arrive over it.
    */
    const docRows =
      catalogAnswered && docsAnswer
        ? docsAnswer.hits.slice(0, DOCS_UNDER_ALL).map(docRow)
        : [];

    // The exact match gets its own heading rather than a second `Navigate`
    // one: the section headers are drawn on every change of section, so
    // splitting one list in two around the entities would print the same word
    // twice down the palette.
    return [
      ...exactPage.map(d => destinationRow(d, 'Go to')),
      ...entities.map(e => entityRow(e)),
      ...otherPages.map(d => destinationRow(d, 'Navigate')),
      ...docRows,
      ...(docRows.length ? [allDocsRow(term)] : []),
    ];
  }, [
    asksQuestion,
    isActionMode,
    scope,
    term,
    entities,
    recent,
    query,
    pathname,
    catalogAnswered,
    docsAnswer,
  ]);

  // The documentation's own state, said under the list, below where its
  // section will stand — so saying it moves none of the rows.
  const docsPending =
    wantsDocs && (!docsAnswer || (scope === 'All' && !catalogAnswered));
  const docsFailed = wantsDocs && !docsPending && Boolean(docsAnswer?.failed);

  const active = Math.max(
    0,
    rows.findIndex(row => row.key === activeKey),
  );

  // The rows in their sections, each section a group named by its heading, so
  // what a section holds can be read without counting rows down the list.
  const sections = useMemo(() => {
    const out: { name: string; rows: { row: Row; index: number }[] }[] = [];
    rows.forEach((row, index) => {
      const last = out[out.length - 1];
      if (last?.name === row.section) {
        last.rows.push({ row, index });
      } else {
        out.push({ name: row.section, rows: [{ row, index }] });
      }
    });
    return out;
  }, [rows]);

  // What the reader types or which scope they pick is a new question, so the
  // highlight goes back to the top; rows arriving for the same question keep
  // it where the reader put it.
  const changeQuery = (next: string) => {
    setQuery(next);
    setActiveKey(null);
  };
  const changeScope = (next: Scope) => {
    setScope(next);
    setActiveKey(null);
  };

  const go = (row: Row) => {
    onClose();
    navigate(row.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'Tab') {
      // Steps the scope, forwards or, with shift, back — the chips are a ring
      // rather than a list with ends, so a reader can reach any of them from
      // any other without changing direction.
      e.preventDefault();
      const i = SCOPES.indexOf(scope);
      const next = e.shiftKey
        ? (i - 1 + SCOPES.length) % SCOPES.length
        : (i + 1) % SCOPES.length;
      changeScope(SCOPES[next]);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveKey(rows[Math.min(active + 1, rows.length - 1)]?.key ?? null);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveKey(rows[Math.max(active - 1, 0)]?.key ?? null);
      return;
    }
    if (e.key === 'Enter' && rows[active]) {
      e.preventDefault();
      go(rows[active]);
    }
  };

  return (
    <div
      className={classes.backdrop}
      role="presentation"
      onMouseDown={e => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/*
        The keys are handled on the panel, so they work wherever the focus
        sits. Binding them to the input alone was wrong: the scope chips and
        the rows are real buttons, and clicking one moves the focus out of the
        input — after which the `esc` badge in the corner stopped closing the
        palette and the arrows stopped moving the highlight, with the palette
        still advertising both.

        `onKeyDown` on a div is flagged by the accessibility rule that wants
        interactive handlers on interactive elements. Here the element IS
        interactive — a modal dialog, labelled and marked as one, holding the
        focus while it is open — which is the case the rule's `role` exception
        is for.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={classes.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
      >
        <div className={classes.inputRow}>
          <SearchIcon fontSize="small" color="disabled" />
          <input
            ref={inputRef}
            className={classes.input}
            value={query}
            onChange={e => changeQuery(e.target.value)}
            placeholder="Search services, docs, APIs, people… or type a command"
            aria-label="Search services, docs, APIs, people, or type a command"
          />
          <span className={classes.esc}>esc</span>
        </div>

        <div className={classes.chips} role="tablist" aria-label="Search scope">
          {SCOPES.map(s => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={scope === s}
              className={`${classes.chip} ${
                scope === s ? classes.chipActive : ''
              }`}
              onClick={() => changeScope(s)}
            >
              {s}
            </button>
          ))}
        </div>

        <div className={classes.list} role="listbox">
          {/* Not while the catalog or the index is still answering: under a
              scope, a term has nothing to show until it does, and "nothing
              matches" would flash on every keystroke. Nor when the index could
              not be asked — nothing was searched, so nothing can be said not
              to match. */}
          {rows.length === 0 &&
            !searching &&
            !docsPending &&
            !docsFailed &&
            (term ? (
              <p className={classes.empty}>
                Nothing matches “{term}”. Press{' '}
                <span className={classes.kbd}>tab</span> to widen the scope, or
                end the line with a question mark to ask.
              </p>
            ) : (
              <p className={classes.empty}>{SCOPE_HINT[scope]}</p>
            ))}
          {sections.map(section => (
            <div key={section.name} role="group" aria-label={section.name}>
              <h2 className={classes.sectionLabel}>{section.name}</h2>
              {section.rows.map(({ row, index }) => (
                <button
                  key={row.key}
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  className={`${classes.row} ${
                    row.excerpt ? classes.rowDoc : ''
                  } ${index === active ? classes.rowActive : ''}`}
                  // On the pointer moving, not on it entering: when Recent
                  // arrives, the rows slide under a pointer that is resting
                  // over the list, and the browser reports that as entering
                  // whichever row is now beneath it — taking the highlight
                  // from the row the reader moved to with the keys.
                  onMouseMove={() =>
                    activeKey !== row.key && setActiveKey(row.key)
                  }
                  onClick={() => go(row)}
                >
                  {row.excerpt ? (
                    <span className={classes.docLine}>
                      <span className={classes.rowTitleDoc}>{row.title}</span>
                      <span className={classes.docSite}>
                        {row.context ?? ''}
                      </span>
                      <span className={classes.docKind}>{row.kind}</span>
                    </span>
                  ) : (
                    <>
                      <span className={classes.rowTitle}>{row.title}</span>
                      <span className={classes.rowContext}>
                        {row.contextRef ? (
                          <EntityDisplayName
                            entityRef={row.contextRef}
                            hideIcon
                            disableTooltip
                          />
                        ) : (
                          (row.context ?? '')
                        )}
                      </span>
                      {row.kind && (
                        <span className={classes.rowKind}>{row.kind}</span>
                      )}
                    </>
                  )}
                  {row.excerpt && (
                    <span className={classes.excerpt}>
                      {row.excerpt.map((part, i) =>
                        part.hit ? (
                          <mark key={i} className={classes.excerptHit}>
                            {part.text}
                          </mark>
                        ) : (
                          <Fragment key={i}>{part.text}</Fragment>
                        ),
                      )}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* The documentation's state, under the list rather than in it: a
            listbox holds options and nothing else. The region is always there
            and only its words change, which is what a screen reader announces
            — one that appears already holding its text is often not read. */}
        <div role="status" aria-live="polite">
          {docsPending && (
            <p className={classes.docsStatus}>Searching the documentation…</p>
          )}
          {docsFailed && (
            <p className={classes.docsStatus}>
              The documentation could not be searched just now.
            </p>
          )}
        </div>

        <div className={classes.footer}>
          <span>
            <span className={classes.kbd}>↑↓</span> navigate
          </span>
          <span>
            <span className={classes.kbd}>↵</span> open
          </span>
          <span>
            <span className={classes.kbd}>tab</span> scope
          </span>
          <span className={classes.footerSpacer} />
          <span>
            <span className={classes.kbd}>&gt;</span> actions · end with{' '}
            <span className={classes.kbd}>?</span> to ask
          </span>
        </div>
      </div>
    </div>
  );
};
